import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import TemuSyncRangeModal from '@/temu-platform/components/TemuSyncRangeModal';
import TemuDateRangeFilter, {
  pickDatePart,
  toDayEndDateTime,
  toDayStartDateTime,
  validateDateRange,
} from '@/temu-platform/components/TemuDateRangeFilter';
import BatchQueryModal from '@/temu-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/temu-platform/modules/products/components/batchQueryUtils';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import HoverCopyField from '@/temu-platform/modules/products/components/HoverCopyField';
import TemuGatewayRegionTabs, { TEMU_GATEWAY_REGION_ALL } from '@/temu-platform/components/TemuGatewayRegionTabs';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { useTemuPageShopScope } from '@/temu-platform/hooks/useTemuPageShopScope';
import { buildTemuPath } from '@/temu-platform/constants/temuRoutes';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { extractTemuList, extractTemuTotal, pickField } from '@/temu-platform/utils/temuResponse';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';

const EMPTY_FILTER = {
  parentAfterSalesSn: '',
  parentOrderSn: '',
  afterSalesStatus: '',
  startTime: '',
  endTime: '',
};

function exportHeaders(t) {
  return [
    t('returnList.semiColSn'),
    t('returnList.relatedOrder'),
    t('returnList.semiRegion'),
    t('returnList.status'),
    t('returnList.semiAmount'),
    t('returnList.semiSyncedAt'),
  ];
}

function rowKey(row, index) {
  return String(pickField(row, 'parentAfterSalesSn', 'parent_after_sales_sn') || `row-${index}`);
}

function validateAftersalesWindow(startDate, endDate) {
  const basic = validateDateRange(startDate, endDate);
  if (basic) return basic;
  const start = pickDatePart(startDate);
  const end = pickDatePart(endDate);
  if (!start || !end) return '';
  const ms = new Date(`${end}T00:00:00`).getTime() - new Date(`${start}T00:00:00`).getTime();
  if (ms > 90 * 24 * 60 * 60 * 1000) {
    return i18n.t('returnList.semiWindow90', { ns: 'workbench' });
  }
  return '';
}

function toExportRow(row) {
  return [
    pickField(row, 'parentAfterSalesSn', 'parent_after_sales_sn') || '',
    pickField(row, 'parentOrderSn', 'parent_order_sn') || '',
    pickField(row, 'gatewayRegion', 'gateway_region') || '',
    pickField(row, 'afterSalesStatus', 'after_sales_status', 'statusLabel') ?? '',
    pickField(row, 'amountSummary', 'amount_summary') || '',
    pickField(row, 'lastSyncedAt', 'last_synced_at') || '',
  ];
}

/**
 * 半托/本土销售售后列表（bg.aftersales.*），与全托退供包裹分流。
 */
export default function SemiReturnOrderListPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const {
    loading: shopLoading,
    concreteScopeId,
  } = useTemuPageShopScope();
  const activeShopId = concreteScopeId;
  const { startJob, onTrackDone, isBusy } = useTemuSyncJobs();
  const pendingSyncRangeRef = useRef(null);
  const selectedRowsRef = useRef(new Map());

  const [gatewayRegion, setGatewayRegion] = useState(TEMU_GATEWAY_REGION_ALL);
  const [filterDraft, setFilterDraft] = useState(() => ({ ...EMPTY_FILTER }));
  const [query, setQuery] = useState(() => ({ ...EMPTY_FILTER, _nonce: 0 }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [syncRangeOpen, setSyncRangeOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [syncingLocal, setSyncingLocal] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchModalField, setBatchModalField] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPage(1);
    setRows([]);
    setTotal(0);
    setError('');
    setLastSyncedAt('');
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [activeShopId, gatewayRegion]);

  const queryParams = useMemo(() => ({
    page: Math.max(0, page - 1),
    size: pageSize,
    gatewayRegion: gatewayRegion || TEMU_GATEWAY_REGION_ALL,
    parentAfterSalesSn: query.parentAfterSalesSn || undefined,
    parentOrderSn: query.parentOrderSn || undefined,
    afterSalesStatus: query.afterSalesStatus || undefined,
    startTime: toDayStartDateTime(query.startTime) || undefined,
    endTime: toDayEndDateTime(query.endTime) || undefined,
    _nonce: query._nonce,
  }), [query, page, pageSize, gatewayRegion]);

  const load = useCallback(async () => {
    if (!activeShopId) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { _nonce, ...params } = queryParams;
      const res = await temuPlatformAPI.listSemiAftersales(activeShopId, params);
      const list = extractTemuList(res);
      setRows(list);
      setTotal(extractTemuTotal(res, list.length));
      if (res?.lastSyncedAt) setLastSyncedAt(res.lastSyncedAt);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e?.message || t('returnList.semiLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeShopId, queryParams, t]);

  useEffect(() => { load(); }, [load]);

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, index)).filter(Boolean),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key, row) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        if (row) selectedRowsRef.current.set(key, row);
      }
      return next;
    });
  };

  useEffect(() => {
    rows.forEach((row, index) => {
      const key = rowKey(row, index);
      if (selectedKeys.has(key)) selectedRowsRef.current.set(key, row);
    });
  }, [rows, selectedKeys]);

  const selectedRows = useMemo(() => {
    const map = selectedRowsRef.current;
    for (const key of [...map.keys()]) {
      if (!selectedKeys.has(key)) map.delete(key);
    }
    rows.forEach((row, index) => {
      const key = rowKey(row, index);
      if (selectedKeys.has(key)) map.set(key, row);
    });
    return Array.from(selectedKeys)
      .map((key) => map.get(key))
      .filter(Boolean);
  }, [rows, selectedKeys]);

  const syncing = syncingLocal || isBusy('returnOrders', activeShopId);

  const handleSync = useCallback(async () => {
    const range = pendingSyncRangeRef.current;
    if (!activeShopId || !range?.startTime || !range?.endTime) return;
    setSyncingLocal(true);
    try {
      const track = await startJob({
        kind: 'returnOrders',
        shopId: activeShopId,
        label: t('returnList.semiSyncLabel'),
        payload: {
          startTime: range.startTime,
          endTime: range.endTime,
          gatewayRegion: gatewayRegion === TEMU_GATEWAY_REGION_ALL ? undefined : gatewayRegion,
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      setLastSyncedAt(new Date().toISOString());
      setQuery((prev) => ({ ...prev, _nonce: (prev._nonce || 0) + 1 }));
      showToast(t('returnList.semiSynced'), 'success');
    } catch (e) {
      showToast(e?.message || t('returnList.syncFailed'), 'error');
    } finally {
      setSyncingLocal(false);
    }
  }, [activeShopId, gatewayRegion, startJob, onTrackDone, t]);

  const onQuery = () => {
    const afterTokens = parseBatchTokens(filterDraft.parentAfterSalesSn).slice(0, BATCH_QUERY_LIMIT);
    const orderTokens = parseBatchTokens(filterDraft.parentOrderSn).slice(0, BATCH_QUERY_LIMIT);
    const err = validateAftersalesWindow(filterDraft.startTime, filterDraft.endTime);
    if (err) {
      showToast(err, 'warning');
      return;
    }
    setQuery({
      ...filterDraft,
      parentAfterSalesSn: afterTokens.join(','),
      parentOrderSn: orderTokens.join(','),
      _nonce: (query._nonce || 0) + 1,
    });
    setPage(1);
  };

  const exportRows = (list, emptyTip) => {
    if (!Array.isArray(list) || list.length === 0) {
      showToast(emptyTip || t('returnList.semiNoneExport'), 'warning');
      return;
    }
    downloadCsv(
      `temu-semi-aftersales-${Date.now()}.csv`,
      exportHeaders(t),
      list.map(toExportRow),
    );
    showToast(t('returnList.exported', { count: list.length }), 'success');
  };

  const exportSelected = () => {
    exportRows(selectedRows, t('returnList.semiPickExport'));
  };

  const exportPage = () => {
    exportRows(rows, t('returnList.semiNoneExport'));
  };

  const exportFiltered = async () => {
    if (!activeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (total <= 0 && rows.length === 0) {
      showToast(t('returnList.semiNoneExport'), 'warning');
      return;
    }
    setExporting(true);
    try {
      const { _nonce, ...baseParams } = queryParams;
      const size = 100;
      let pageNo = 0;
      let expectedTotal = Number.isFinite(total) && total > 0 ? total : Infinity;
      const all = [];
      while (all.length < expectedTotal) {
        const res = await temuPlatformAPI.listSemiAftersales(activeShopId, {
          ...baseParams,
          page: pageNo,
          size,
        });
        const list = extractTemuList(res);
        expectedTotal = extractTemuTotal(res, list.length);
        all.push(...list);
        if (!list.length || list.length < size) break;
        pageNo += 1;
        if (pageNo > 500) break;
      }
      exportRows(all, t('returnList.semiNoneExport'));
    } catch (e) {
      showToast(e?.message || t('returnList.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const onBatchDelete = async () => {
    if (!activeShopId || selectedKeys.size === 0) {
      showToast(t('returnList.semiPickDelete'), 'warning');
      return;
    }
    const sns = selectedRows
      .map((row) => pickField(row, 'parentAfterSalesSn', 'parent_after_sales_sn'))
      .filter((no) => no != null && String(no).trim() !== '')
      .map((no) => String(no).trim());
    if (!sns.length) {
      showToast(t('returnList.semiMissingSn'), 'error');
      return;
    }
    const ok = await showTemuConfirm({
      title: t('returnList.batchDelete'),
      message: t('returnList.semiDeleteMsg', { count: sns.length }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await temuPlatformAPI.batchDeleteSemiAftersales(activeShopId, {
        parentAfterSalesSns: sns,
      });
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : sns.length;
      if (count > 0) {
        showToast(t('returnList.semiDeleted', { count }), 'success');
      } else {
        showToast(t('returnList.noneDeleted'), 'warning');
      }
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      load();
    } catch (e) {
      showToast(e?.message || t('returnList.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const emptyTitle = !activeShopId
    ? t('returnList.pickShop')
    : error
      ? error
      : loading
        ? t('returnList.loading')
        : t('returnList.semiEmpty');
  const emptyDesc = activeShopId && !loading && !error && rows.length === 0
    ? t('returnList.semiEmptyPull')
    : '';

  return (
    <>
      <TemuSyncProgressSlot kinds={['returnOrders']} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || exporting || deleting}
            syncKinds={['returnOrders']}
            onSync={() => {
              if (!activeShopId) {
                showToast(t('returnList.semiPickShop'), 'warning');
                return;
              }
              setSyncRangeOpen(true);
            }}
            syncing={syncing}
            syncDisabled={shopLoading || !activeShopId || syncing}
            lastSyncedLabel={formatSiteListSyncedAt(lastSyncedAt)}
            syncHint={t('returnList.semiSyncHint')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={exporting || !activeShopId}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            batchOpsMenu={[
              {
                key: 'batch-delete',
                label: selectedKeys.size > 0 ? t('returnList.semiBatchDeleteCount', { count: selectedKeys.size }) : t('returnList.batchDelete'),
                tone: 'danger',
                disabled: !activeShopId || deleting || selectedKeys.size === 0,
                onClick: onBatchDelete,
              },
            ]}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--id-combo tpo-field--batch tpo-field--w300">
              <input
                className="tpo-input"
                value={filterDraft.parentAfterSalesSn}
                placeholder={t('returnList.semiParentPh')}
                disabled={!activeShopId}
                onChange={(e) => setFilterDraft((p) => ({ ...p, parentAfterSalesSn: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={t('returnList.semiBatchParent')}
                title={t('returnList.batchQuery')}
                disabled={!activeShopId}
                onClick={() => {
                  setBatchModalField('parentAfterSalesSn');
                  setBatchModalOpen(true);
                }}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="tpo-field tpo-field--inline tpo-field--id-combo tpo-field--batch tpo-field--w300">
              <input
                className="tpo-input"
                value={filterDraft.parentOrderSn}
                placeholder={t('returnList.semiOrderPh')}
                disabled={!activeShopId}
                onChange={(e) => setFilterDraft((p) => ({ ...p, parentOrderSn: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={t('returnList.semiBatchOrder')}
                title={t('returnList.batchQuery')}
                disabled={!activeShopId}
                onClick={() => {
                  setBatchModalField('parentOrderSn');
                  setBatchModalOpen(true);
                }}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <TemuDateRangeFilter
              startValue={filterDraft.startTime}
              endValue={filterDraft.endTime}
              disabled={!activeShopId}
              onStartChange={(v) => setFilterDraft((p) => ({ ...p, startTime: v }))}
              onEndChange={(v) => setFilterDraft((p) => ({ ...p, endTime: v }))}
            />
          </div>
          <div className="tpo-filter-bar__actions">
            <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" disabled={!activeShopId} onClick={() => {
              setFilterDraft({ ...EMPTY_FILTER });
              setQuery({ ...EMPTY_FILTER, _nonce: (query._nonce || 0) + 1 });
              setPage(1);
            }}>
              {t('returnList.reset')}
            </button>
            <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" disabled={!activeShopId || loading} onClick={onQuery}>
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <BatchQueryModal
        open={batchModalOpen}
        title={batchModalField === 'parentOrderSn' ? t('returnList.semiBatchOrder') : t('returnList.semiBatchParent')}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={t('returnList.batchLinePh')}
        onConfirm={(tokens) => {
          if (batchModalField) {
            setFilterDraft((p) => ({ ...p, [batchModalField]: joinBatchTokens(tokens) }));
          }
          setBatchModalOpen(false);
          setBatchModalField(null);
        }}
        onClose={() => {
          setBatchModalOpen(false);
          setBatchModalField(null);
        }}
      />

      <TemuSyncRangeModal
        open={syncRangeOpen}
        title={t('returnList.semiSyncLabel')}
        defaultHours={24 * 7}
        maxHours={24 * 90}
        hint={t('returnList.semiSyncModalHint')}
        presets={[
          { label: t('returnList.last24h'), hours: 24 },
          { label: t('returnList.last7d'), hours: 24 * 7 },
          { label: t('returnList.last30d'), hours: 24 * 30 },
          { label: t('returnList.semiLast90d'), hours: 24 * 90 },
        ]}
        confirming={syncing}
        onConfirm={async (range) => {
          pendingSyncRangeRef.current = range;
          setSyncRangeOpen(false);
          await handleSync();
        }}
        onClose={() => {
          if (!syncing) setSyncRangeOpen(false);
        }}
      />

      <ProductListCard
        loading={Boolean(activeShopId) && loading}
        loadingLabel={t('returnList.semiQuerying')}
        tabs={(
          <TemuGatewayRegionTabs
            shopId={activeShopId}
            value={gatewayRegion}
            onChange={(next) => {
              setGatewayRegion(next);
              setPage(1);
            }}
          />
        )}
      >
        <div className="tpo-drafts__table-wrap tpo-ops-table-wrap">
          <table className="tpo-publish__table tpo-drafts__table tpo-ops-table">
            <colgroup>
              <col className="tpo-ops-col tpo-ops-col--check temu-product-col--check" />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-ops-th tpo-ops-th--check temu-product-cell--spu-check"
                  onClick={() => {
                    if (!activeShopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('returnList.semiSelectAll')}
                    disabled={!activeShopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col">{t('returnList.semiParentCol')}</th>
                <th scope="col">{t('returnList.relatedOrder')}</th>
                <th scope="col">{t('returnList.semiRegion')}</th>
                <th scope="col">{t('returnList.status')}</th>
                <th scope="col">{t('returnList.semiAmount')}</th>
                <th scope="col">{t('returnList.semiSyncedAt')}</th>
              </tr>
            </thead>
            <tbody>
              {!activeShopId || loading || rows.length === 0 || error ? (
                <tr>
                  <td colSpan={7} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">{emptyTitle}</p>
                      {emptyDesc ? <p className="tpo-certs-empty__desc">{emptyDesc}</p> : null}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const key = rowKey(row, index);
                  const sn = pickField(row, 'parentAfterSalesSn', 'parent_after_sales_sn');
                  const region = pickField(row, 'gatewayRegion', 'gateway_region') || '—';
                  return (
                    <tr
                      key={key}
                      className="tpo-table__row--clickable"
                      onClick={() => {
                        const q = new URLSearchParams();
                        if (region && region !== '—') q.set('gatewayRegion', region);
                        navigate(buildTemuPath(`orders/returns/${encodeURIComponent(sn)}${q.toString() ? `?${q}` : ''}`));
                      }}
                    >
                      <td
                        className="tpo-ops-td tpo-ops-td--check temu-product-cell--spu-check"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOne(key, row);
                        }}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('returnList.selectRow', { title: sn || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key, row)}
                        />
                      </td>
                      <td><HoverCopyField value={sn} /></td>
                      <td><HoverCopyField value={pickField(row, 'parentOrderSn', 'parent_order_sn')} /></td>
                      <td>{region}</td>
                      <td>{pickField(row, 'afterSalesStatus', 'after_sales_status', 'statusLabel') ?? '—'}</td>
                      <td>{pickField(row, 'amountSummary', 'amount_summary') || '—'}</td>
                      <td>{formatSiteListSyncedAt(pickField(row, 'lastSyncedAt', 'last_synced_at'))}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!activeShopId}
          pageItemCount={rows.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('returnList.semiPager')}
        />
      </ProductListCard>
    </>
  );
}
